/**
 * Pure view model of the mailing LISTS surface (#34, T.Z132).
 *
 * Framework-free on purpose: the `unit` Playwright project runs in plain Node,
 * where neither React nor `import.meta.env` exists — nothing here value-imports
 * the api layer (type-only imports are erased at runtime), the same rule the
 * sibling `mailings-model.ts` documents.
 *
 * The four selection modes the owner dictates map onto THREE wire modes: the
 * api's `MailingListSelectionMode` is `IDS | STATUS | STATUSES` and «по одному
 * id» is simply an IDS selection of one token — the same mode the «списком id»
 * textarea builds, so the api never learns a fourth word (additive-only, zero
 * contract change).
 */

import type {
  MailingChannel,
  MailingListCountsDto,
  MailingListDto,
  MailingListRecipientDto,
  MailingListResolveReportDto,
  MailingListSelectionMode,
  MailingListTemplatePinDto,
} from '../../api/mailing-lists';
import type { ApplicationDto } from '../../api/applications';

/** Re-exported so the components read one vocabulary module. */
export type {
  MailingChannel,
  MailingListCountsDto,
  MailingListDto,
  MailingListRecipientDto,
  MailingListResolveReportDto,
  MailingListSelectionMode,
  MailingListTemplatePinDto,
};

// ------------------------------------------------------------- vocabulary --

/** The three senders the owner names — offered, not enforced (the api accepts any address). */
export const KNOWN_SENDERS: ReadonlyArray<string> = [
  'support_cf@ismart.org',
  'cof@ismart.org',
  'noreply@ismart.org',
];

/** The label a NEW list's «тип рассылки» field starts with (the owner's standing example). */
export const DEFAULT_MAILING_TYPE = 'Рассылка не подписавшим договор';

// ------------------------------------------------------------- the channel --

/** The two channels a list rides, in the «Создать список» select's order (T.Z210). */
export const LIST_CHANNEL_OPTIONS: ReadonlyArray<{ value: MailingChannel; label: string; hint: string }> = [
  { value: 'EMAIL', label: 'Email', hint: 'Письма уходят через почтовый сервис — получателю нужен e-mail.' },
  { value: 'SMS', label: 'SMS', hint: 'Сообщения уходят через SMS Aero — получателю нужен телефон.' },
];

/** The channel a stored list rides — an api older than v1.9.72 means EMAIL. */
export function channelOf(list: Pick<MailingListDto, 'channel'>): MailingChannel {
  return list.channel ?? 'EMAIL';
}

export function channelLabel(channel: MailingChannel): string {
  return channel === 'SMS' ? 'SMS' : 'Email';
}

/** The «От кого» value an SMS list carries — the field has no SMS meaning (the
 * sender is the registered sign), but the wire keeps requiring the address. */
export const SMS_PLACEHOLDER_SENDER = KNOWN_SENDERS[0] ?? 'support_cf@ismart.org';

/** The four CREATE/EDIT modes the UI speaks — ONE_ID folds onto the wire's IDS. */
export type SelectionUiMode = 'ONE_ID' | 'IDS' | 'STATUS' | 'STATUSES';

export const SELECTION_UI_MODES: ReadonlyArray<{ value: SelectionUiMode; label: string; hint: string }> = [
  { value: 'ONE_ID', label: 'По одному id', hint: 'Один ребёнок по ЕПГУ или УНТИ id — список покажет, кто это.' },
  { value: 'IDS', label: 'Списком id', hint: 'До 1000 ЕПГУ/УНТИ id — в столбик или через запятую.' },
  { value: 'STATUS', label: 'По статусу', hint: 'Все дети одного статуса воронки на момент пересчёта.' },
  { value: 'STATUSES', label: 'По нескольким статусам', hint: 'Любая комбинация статусов воронки.' },
];

export function selectionModeLabel(mode: MailingListSelectionMode): string {
  return mode === 'IDS' ? 'Списком id' : mode === 'STATUS' ? 'По статусу' : 'По нескольким статусам';
}

/** The wire mode a UI mode builds. */
export function wireMode(mode: SelectionUiMode): MailingListSelectionMode {
  return mode === 'STATUS' ? 'STATUS' : mode === 'STATUSES' ? 'STATUSES' : 'IDS';
}

/** The six funnel statuses, in the contract's order — the STATUS/STATUSES pickers iterate THIS list. */
export const SELECTION_STATUSES = [
  'WAITING_LINK',
  'LINK_RECEIVED',
  'EVI_PASSED',
  'REJECTED',
  'AWAITING_INFO',
  'NO_DATA',
] as const;

export type SelectionStatus = (typeof SELECTION_STATUSES)[number];

/**
 * THE RU status vocabulary — the dashboard's own map (`../dashboard/status`
 * keeps THE copy; it is a plain-Node-safe module, so the unit project can still
 * load this file).
 */
import { STATUS_LABEL, STATUS_TONE } from '../dashboard/status';

export { STATUS_LABEL, STATUS_TONE };

/** `MailingListCountsDto.byStatus` serialises camelCase; the pickers speak AppStatus. */
export const STATUS_COUNT_KEY: Readonly<Record<SelectionStatus, keyof MailingListCountsDto['byStatus']>> = {
  WAITING_LINK: 'waitingLink',
  LINK_RECEIVED: 'linkReceived',
  EVI_PASSED: 'eviPassed',
  REJECTED: 'rejected',
  AWAITING_INFO: 'awaitingInfo',
  NO_DATA: 'noData',
};

export const TRACK_LABELS: Record<string, string> = {
  AI: 'ИИ',
  PYTHON: 'Python',
};

export function trackLabel(track: string, resolved: boolean): string {
  const base = TRACK_LABELS[track] ?? track;
  return resolved ? base : `${base} (не определён)`;
}

// --------------------------------------------------------------- state tabs --

export type ListStateFilter = 'active' | 'removed' | 'all';

export const STATE_TABS: ReadonlyArray<{ value: ListStateFilter; label: string }> = [
  { value: 'active', label: 'Активные' },
  { value: 'removed', label: 'Выбывшие' },
  { value: 'all', label: 'Все' },
];

/** The number a state tab shows — `?state=active` is what a send would mail. */
export function stateTabCount(state: ListStateFilter, counts: MailingListCountsDto | null | undefined): number {
  if (!counts) return 0;
  if (state === 'active') return counts.total;
  if (state === 'removed') return counts.removed;
  return counts.total + counts.removed;
}

// ------------------------------------------------------ selection payloads --

/**
 * Splits the «списком id» textarea into tokens: any mix of newlines, commas,
 * semicolons and whitespace separates; empties drop, duplicates collapse
 * (the api collapses them silently anyway — the count the operator sees should
 * be the count the resolve will use).
 */
export function parseIdsTextarea(text: string): string[] {
  const seen = new Set<string>();
  for (const raw of text.split(/[\s,;]+/)) {
    const token = raw.trim();
    if (token.length > 0) seen.add(token);
  }
  return [...seen];
}

export interface SelectionInput {
  mode: SelectionUiMode;
  /** ONE_ID: exactly one token. IDS: the parsed textarea tokens. */
  ids?: string[];
  /** STATUS: one value. STATUSES: 1..6 of them (order = the picker's). */
  statuses?: string[];
}

export type SelectionBuildResult =
  | { ok: true; selectionMode: MailingListSelectionMode; selectionPayload: Record<string, unknown> }
  | { ok: false; error: string };

/**
 * Builds the (mode, payload) pair #34.2/#34.4 carry. Every refusal the UI can
 * predict it refuses HERE, in RU, before a round trip — the api's 400
 * SELECTION_INVALID stays the authority for anything that slips through.
 */
export function buildSelection(input: SelectionInput): SelectionBuildResult {
  if (input.mode === 'ONE_ID') {
    const ids = (input.ids ?? []).map((token) => token.trim()).filter((token) => token.length > 0);
    if (ids.length === 0) return { ok: false, error: 'Укажите ЕПГУ или УНТИ id ребёнка.' };
    if (ids.length > 1) return { ok: false, error: 'В этом режиме — один id. Несколько — режим «Списком id».' };
    return { ok: true, selectionMode: 'IDS', selectionPayload: { ids } };
  }
  if (input.mode === 'IDS') {
    const ids = input.ids ?? [];
    if (ids.length === 0) return { ok: false, error: 'Вставьте хотя бы один id.' };
    // T.Z205 — the id CAP is gone (owner 25.09): the api no longer refuses a big
    // paste, so neither does the UI. The request body limit stays the bound.
    return { ok: true, selectionMode: 'IDS', selectionPayload: { ids } };
  }
  if (input.mode === 'STATUS') {
    const status = (input.statuses ?? [])[0];
    if (!status || !isSelectionStatus(status)) return { ok: false, error: 'Выберите статус воронки.' };
    return { ok: true, selectionMode: 'STATUS', selectionPayload: { status } };
  }
  const statuses = (input.statuses ?? []).filter(isSelectionStatus);
  if (statuses.length === 0) return { ok: false, error: 'Отметьте хотя бы один статус.' };
  if (statuses.length > SELECTION_STATUSES.length) {
    return { ok: false, error: 'Отмечены неизвестные статусы.' };
  }
  return { ok: true, selectionMode: 'STATUSES', selectionPayload: { statuses } };
}

function isSelectionStatus(value: string): value is SelectionStatus {
  return (SELECTION_STATUSES as readonly string[]).includes(value);
}

/**
 * Reads a stored selection back into words the index row and the list header
 * show. The payload is the api's own parse (it stores what resolved), so the
 * fold here is display-only: an unparseable payload degrades to a dash, never
 * to a thrown error — a list must always open.
 */
export function describeSelection(
  mode: MailingListSelectionMode,
  payload: Record<string, unknown> | null | undefined,
): string {
  if (!payload || typeof payload !== 'object') return '—';
  if (mode === 'IDS') {
    const ids = Array.isArray(payload['ids']) ? payload['ids'].filter((id): id is string => typeof id === 'string') : [];
    if (ids.length === 0) return '—';
    const head = ids.slice(0, 3).join(', ');
    return ids.length <= 3 ? `по id: ${head}` : `по id: ${head} и ещё ${ids.length - 3}`;
  }
  if (mode === 'STATUS') {
    const status = payload['status'];
    const label = typeof status === 'string' ? statusLabel(status) : undefined;
    return label ? `по статусу: ${label}` : '—';
  }
  const statuses = Array.isArray(payload['statuses'])
    ? payload['statuses'].filter((status): status is string => typeof status === 'string')
    : [];
  if (statuses.length === 0) return '—';
  const labels = statuses.map((status) => statusLabel(status));
  return `по статусам: ${labels.join(', ')}`;
}

/** STATUS_LABEL is total over the six codes; a foreign code degrades to itself. */
function statusLabel(status: string): string {
  return STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status;
}

// ------------------------------------------------------------ who preview —

export interface WhoPreviewMatch {
  pguId: string | null;
  untiId: string | null;
  fullName: string | null;
  email: string | null;
  status: string | null;
  /** true = pgu/unti matched the token EXACTLY (the resolve will find him). */
  exact: boolean;
}

/**
 * Folds the register search (`?q=`) into the single-id mode's live «кто это»
 * answer. An EXACT pgu/unti hit wins over an ILIKE contains-hit; the resolve
 * report stays the authority — this is a convenience read over #3.
 */
export function pickWhoMatch(rows: ApplicationDto[], token: string): WhoPreviewMatch | null {
  const needle = token.trim();
  if (needle.length === 0) return null;
  const exact = rows.find((row) => row.pguId === needle || row.untiId === needle);
  if (exact) {
    return { pguId: exact.pguId, untiId: exact.untiId, fullName: exact.fullName, email: exact.email, status: exact.status, exact: true };
  }
  const first = rows[0];
  if (!first) return null;
  return {
    pguId: first.pguId,
    untiId: first.untiId,
    fullName: first.fullName,
    email: first.email,
    status: first.status,
    exact: false,
  };
}

export function formatWhoMatch(match: WhoPreviewMatch | null): string {
  if (!match) return 'Ребёнок с таким id в реестре не нашёлся — он попадёт в отчёт пересчёта как ненайденный.';
  const who = match.fullName ?? '(без ФИО)';
  const ids = [match.pguId ? `ЕПГУ ${match.pguId}` : null, match.untiId ? `УНТИ ${match.untiId}` : null]
    .filter((value): value is string => value !== null)
    .join(' · ');
  const status = match.status ? (STATUS_LABEL[match.status as keyof typeof STATUS_LABEL] ?? match.status) : null;
  const suffix = match.exact ? '' : ' (совпадение по части id — проверьте)';
  return [who, ids, status].filter((value) => value !== null && value !== '').join(' · ') + suffix;
}

// ------------------------------------------------------------ the counts ——

export interface CountTile {
  key: string;
  value: number;
  label: string;
  tone: 'brand' | 'neutral' | 'ok' | 'bad' | 'warn';
  hint?: string;
}

/**
 * The list view's stat tiles — the T.Z128 grammar (`.mailings-chip`), the same
 * tile array the classic page builds its counters from. Live/removed/renderReady
 * first, then the funnel statuses in the contract's order, then the tracks.
 */
export function countTiles(counts: MailingListCountsDto | null | undefined): CountTile[] {
  if (!counts) return [];
  const tiles: CountTile[] = [
    { key: 'total', value: counts.total, label: 'активных', tone: 'brand' },
    {
      key: 'render-ready',
      value: counts.renderReady,
      label: 'готовы к отправке',
      tone: 'ok',
      hint: 'Есть и адрес, и имя для обращения — письмо соберётся как есть.',
    },
    { key: 'removed', value: counts.removed, label: 'выбыли', tone: 'warn', hint: 'Перестали подходить под отбор на последнем пересчёте — строки сохранены.' },
  ];
  for (const status of SELECTION_STATUSES) {
    tiles.push({
      key: `status-${status.toLowerCase()}`,
      value: counts.byStatus[STATUS_COUNT_KEY[status]],
      label: statusLabel(status),
      tone: status === 'EVI_PASSED' ? 'ok' : 'neutral',
    });
  }
  tiles.push({ key: 'track-ai', value: counts.byTrack.ai, label: 'программы ИИ', tone: 'brand' });
  tiles.push({ key: 'track-python', value: counts.byTrack.python, label: 'программы Python', tone: 'brand' });
  tiles.push({
    key: 'track-unresolved',
    value: counts.byTrack.unresolved,
    label: 'трек не определён',
    tone: 'neutral',
    hint: 'Письмо уйдёт на Python — приветственную страницу того трека.',
  });
  return tiles;
}

// ----------------------------------------------------------- the list row —

export function isArchived(list: Pick<MailingListDto, 'archivedAt'>): boolean {
  return list.archivedAt !== null;
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU');
}

export function formatTemplatePin(template: MailingListTemplatePinDto | null | undefined): string {
  if (!template) return '—';
  return `${template.title} · версия ${template.templateVersion}`;
}

/** One line of the resolve report — added / refreshed / reactivated / removed. */
export function formatResolveReport(report: MailingListResolveReportDto): string {
  const parts = [
    `в списке ${report.total}`,
    `добавлено ${report.added}`,
    `обновлено ${report.refreshed}`,
    report.reactivated > 0 ? `вернулось ${report.reactivated}` : null,
    `выбыло ${report.removed}`,
  ].filter((part): part is string => part !== null);
  const missingIds = report.missingIds ?? [];
  const missing =
    report.missingCount !== null && report.missingCount !== undefined && report.missingCount > 0
      ? ` · не нашлось ${report.missingCount}${
          missingIds.length > 0 ? ` (первые: ${missingIds.slice(0, 5).join(', ')})` : ''
        }`
      : '';
  return `Пересчёт ${formatWhen(report.resolvedAt)}: ${parts.join(', ')}${missing}`;
}

/** The recipients table's honest badge for a row a letter cannot be built from. */
export function renderReadyLabel(recipient: Pick<MailingListRecipientDto, 'renderReady' | 'removedAt'>): { label: string; tone: 'ok' | 'warn' | 'neutral' } {
  if (recipient.removedAt !== null) return { label: 'Выбыл', tone: 'neutral' };
  return recipient.renderReady ? { label: 'Готов', tone: 'ok' } : { label: 'Нет данных', tone: 'warn' };
}

// --------------------------------------------------------------- URL params --

export const LIST_PARAM = {
  TAB: 'tab',
  LIST: 'list',
  STATE: 'state',
  PAGE: 'page',
} as const;

export const TAB_LISTS = 'lists';
export const TAB_CLASSIC = 'classic';
/** T.Z134 — the templates management tab (contract #33), the third of the three. */
export const TAB_TEMPLATES = 'templates';

export type MailingsTab = 'lists' | 'classic' | 'templates';

export function parseTabParam(value: string | null | undefined): MailingsTab {
  if (value === TAB_CLASSIC) return 'classic';
  if (value === TAB_TEMPLATES) return 'templates';
  return 'lists';
}

export function parseListIdParam(value: string | null | undefined): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : null;
}

export function parseStateParam(value: string | null | undefined): ListStateFilter {
  return value === 'removed' || value === 'all' ? value : 'active';
}

export function parsePageParam(value: string | null | undefined): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
}

// ------------------------------------------------------------- send engine --

/**
 * T.Z133 feature detection. The send engine is a SIBLING track that may land
 * before, with or after this UI; until its routes exist the buttons stay
 * visible but disabled with THIS hint. The probe itself (GET-only, so it can
 * never send a letter) and the paths it reads live in `api/mailing-lists.ts`
 * — the transport's business — while the words the page shows live here.
 */
export const SEND_UNAVAILABLE_HINT = 'Отправка писем по списку — следующее обновление: движок рассылки ещё не подключён.';

/**
 * What a probe answer means. 404 = the route does not exist (Nest answers 404
 * to both an unknown path AND a method mismatch, so a GET probe is safe against
 * a POST-only send route). ANY other answer — 200, 401, 403, 400, 500 — proves
 * SOMETHING is mounted there, which is all the detection needs: the buttons
 * unlock, and the engine's own errors speak for themselves from then on.
 */
export function sendProbeAnswered(status: number): boolean {
  return status !== 404;
}

// ----------------------------------------------------------- the status panel --

/** The #34.10 run, as the status panel's progress line reads it. */
export type ListRunProgress = {
  processed: number;
  total: number;
  accepted: number;
  failed: number;
  skipped: number;
};

/** The #34.10 ledger, as the status panel's chips read it. */
export type ListDeliveryLedger = {
  accepted: number;
  delivered: number;
  failed: number;
  skipped: number;
  /** ADDITIVE v1.9.56 — may be absent on an api that predates the field. */
  notReceived?: number;
};

/** The run's own progress line — the fixed panels' words, the list's numbers. */
export function formatListRunProgress(run: ListRunProgress): string {
  return `Отправлено ${run.processed} из ${run.total} — принято ${run.accepted}, ошибок ${run.failed}, пропущено ${run.skipped}`;
}

/**
 * The «Рассылка запущена» line of the send-all (T.Z185). The payload is the
 * ENVELOPE'S `data` — the api (#34.9) wraps `{queued, runId, listId, total,
 * skippedAlreadySent}` in the house `data` envelope, and reading the keys off
 * the envelope itself is exactly what once rendered «Рассылка запущена:
 * undefined получателей (запуск undefined)» while the letters went out fine.
 * ONE fold here, so the view cannot re-invent the mistake.
 */
export function formatSendAllNotice(payload: { total: number; runId: string }): string {
  return `Рассылка запущена: ${payload.total} получателей (запуск ${payload.runId}).`;
}

/**
 * The five delivery chips of a list's own sends (T.Z185, the owner's «add
 * status in next wave for this mailings») — the fixed panels' grammar, computed
 * from #34.10 and #34.3. The channel names two of them (T.Z210): on an SMS list
 * «не получил» reads «не доставлено» — nothing is «received» on a phone, only
 * the gate's own delivery verdict is a fact — and the «принято» hint speaks of
 * messages, not letters:
 *
 *   · всего получателей  — the list's LIVE snapshot (#34.3 `counts.total`);
 *   · принято            — the ACCEPTED rows of THIS list (shown prominently:
 *                          «доставлено» is capped at «принято» until a Data
 *                          Streams bridge exists, so accepted is the honest
 *                          head number of a fresh send);
 *   · доставлено         — the DELIVERED rows (SES confirmed, via the recheck);
 *   · ошибок             — the FAILED rows;
 *   · не получил         — `ledger.notReceived` (the api's own count of the live
 *                          recipients with no ACCEPTED/DELIVERED letter of THIS
 *                          list); an api older than v1.9.56 leaves it absent and
 *                          the chip degrades to `total − accepted − delivered`,
 *                          floored at zero, never negative.
 */
export function deliveryTiles(
  total: number,
  ledger: ListDeliveryLedger | null | undefined,
  channel: MailingChannel = 'EMAIL',
): CountTile[] {
  const sms = channel === 'SMS';
  const accepted = ledger?.accepted ?? 0;
  const delivered = ledger?.delivered ?? 0;
  const failed = ledger?.failed ?? 0;
  const notReceived = ledger?.notReceived ?? Math.max(0, total - accepted - delivered);
  return [
    { key: 'delivery-total', value: total, label: 'всего получателей', tone: 'brand' },
    {
      key: 'delivery-accepted',
      value: accepted,
      label: 'принято',
      tone: 'ok',
      hint: sms
        ? 'Сообщений, которые SMS Aero принял к отправке. Свежая рассылка живёт в этом числе.'
        : 'Писем, которые почтовый сервис принял к отправке. Свежая рассылка живёт в этом числе.',
    },
    {
      key: 'delivery-delivered',
      value: delivered,
      label: 'доставлено',
      tone: 'ok',
      hint: sms
        ? 'Подтверждено SMS Aero (статус «Доставлено»). Пока проверка статусов не отвечает — число остаётся ниже «принято».'
        : 'Подтверждено сервисом отправки. Пока проверка доставки не отвечает — число остаётся ниже «принято».',
    },
    { key: 'delivery-failed', value: failed, label: 'ошибок', tone: 'bad' },
    {
      key: 'delivery-not-received',
      value: notReceived,
      label: sms ? 'не доставлено' : 'не получил',
      tone: 'warn',
      hint: sms
        ? 'Активные получатели списка, чьего сообщения этого списка ещё нет в журнале (или оно не подтвердилось).'
        : 'Активные получатели списка, чьего письма этого списка ещё нет в журнале.',
    },
  ];
}

/**
 * The SHORT-LINK clicks tile (ADDITIVE v1.9.73, T.Z211) — ONE chip after the
 * delivery chips, read off #34.10's optional `clicks` trio: «перешли по ссылке»
 * counts the live recipients whose short link was opened at least once, out of
 * the `linked` who own one. NOTHING renders when the api predates the field or
 * when nobody in the list owns a code — a pre-T.Z211 campaign has no clicks to
 * speak of, and an empty chip row is not a number to misread as zero interest.
 */
export function clickTiles(clicks: { linked: number; clicked: number; notClicked: number } | null | undefined): CountTile[] {
  if (!clicks || clicks.linked === 0) return [];
  return [
    {
      key: 'delivery-clicked',
      value: clicks.clicked,
      label: 'перешли по ссылке',
      tone: 'ok',
      hint: `Получателей с короткой ссылкой: ${clicks.linked}, из них не перешли: ${clicks.notClicked}. Ссылка своя у каждого получателя и каждой рассылки.`,
    },
  ];
}
